/* eslint-disable no-unused-vars */
// Preact
// eslint-disable-next-line no-unused-vars
import { Fragment, h } from 'preact';
import { useContext, useEffect, useRef, useState } from 'preact/hooks';
import classnames from 'classnames';
import Facet from './Facet';
import { SearchContext } from '../contexts/Search';
import { serializeObject } from '../util';
import Search from './Search';

const { __ } = wp.i18n;

/**
 * The main component.
 *
 * @return {Object} JSX
 */
const Form = () => {
	const ref = useRef( null );
	const { state, helpers } = useContext( SearchContext );
	const { update } = helpers;
	const { loading, facets, params, filterQueries, foundPosts } = state;
	const [isHidden, setIsHidden] = useState( true );
	const [breakpointMatches, setBreakpointMatches] = useState( true );
	const uniqueId = 'faceted-search-container';

	/**
	 * Handles the form interactions.
	 *
	 * @param {Object} event The event object.
	 */
	const handleFormOnInteraction = ( event ) => {

		event.preventDefault();

		// Don't auto-submit if we are just typing in the search field.
		// We have a submit button next to it.
		const isInputSearch = event.target.closest( '.facet--search' ) && event.target.tagName === 'INPUT';

		if (isInputSearch) {
			return;
		}

		// Don't submit if there are invalid inputs.
		if ( ref.current.querySelector( '.is-invalid, :invalid' ) ) {
			return;
		}

		// Bail if loading.
		if ( loading ) {
			return;
		}

		// Serialize the form for updated fields.
		const serialized = serializeObject( ref.current );

		// Sort by Most relevant if it's happening a search
		if ( ( serialized['filter_queries[]'] != undefined ) && serialized['filter_queries[]'].length ) {
			serialized['sort_by'] = 'most-relevant';
		}

		const updatedParams = { ...serialized, page: 1 };

		// Send a request.
		update( updatedParams );
	};

	/**
	 * Handle toggle click
	 */
	const handleToggleOnClick = () => {
		setIsHidden( !isHidden );
	};

	// On component mount
	useEffect( () => {
		const mq = window.matchMedia( '(min-width: 1084px)' );
		/**
		 * Check media query.
		 */
		const checkMq = () => {
			if ( mq.matches ) {
				setIsHidden( false );
				setBreakpointMatches( true );
			} else {
				setIsHidden( true );
				setBreakpointMatches( false );
			}
		};
		mq.addListener( checkMq );
		checkMq();
	}, [] );

	return (
		<div>
			<button
				id="facet-toggle"
				className="faceted-search__toggle-button btn btn-primary btn-block mb-3"
				type="button"
				onClick={handleToggleOnClick}
				aria-controls={uniqueId}
				aria-expanded={!isHidden}
				style={breakpointMatches ? 'display: none;' : ''}>
				<span>{__( 'Refine results', 'ms-research' )}</span>
				&nbsp;
				<span
					aria-hidden="true"
					className="glyph-prepend glyph-prepend-chevron-down d-inline-block"
					style={!isHidden ? 'transform: rotate(180deg);' : ''}>
				</span>
			</button>
			<div
				id={uniqueId}
				className="faceted-search__toggle-content pb-5"
				aria-hidden={isHidden}
				aria-labelledby={`${uniqueId}-heading`}
				style={isHidden ? 'display: none;' : ''}
				role="region">

				<div className={classnames( 'faceted-search__sidebar-inner', 'border-inverse', 'material-card','px-3','py-4','depth-16', { 'faceted-search__sidebar-inner--is-loading': loading } )}>
					<h2 id={`${uniqueId}-heading`} className="faceted-search__heading h6 m-0">
						{__( 'Refine results', 'ms-research' )}
					</h2>
					<div className="sr-only" id="faceted-search-msg">
						{__(
							'This form will update the search results and the form fields automatically when input state is changed or when the form is submitted. Currently selected items and filters are under the "current selections" heading and can be clicked to remove them from filtering.',
							'ms-research'
						)}
					</div>
					<form
						className="faceted-search__form"
						id="faceted-search-form"
						onSubmit={handleFormOnInteraction}
						onChange={handleFormOnInteraction}
						ref={ref}
						autoComplete="off"
						aria-labelledby={`${uniqueId}-heading`}
						aria-describedby="faceted-search-msg">

						{/* Only include the primary search query param if it actually has a value to keep initial URL clean */}
						{ params.q ? (
							<input type="hidden" value={params.q} name="q" />
						) : null }
						{ filterQueries.length ? (
							filterQueries.map( ( filterQuery, index ) => <input key={ `filter-query-${ index }` } type="hidden" value={ filterQuery } name="filter_queries[]" /> )
						) : null }

						<div className="form-filters">
							{ 0 !== foundPosts &&
								<div className="form-filters__group border-bottom py-4">
									<Search label={__( 'Search within these results', 'ms-research' )} group="filter_queries[]" />
								</div>
							}
							{facets &&
								facets.map(
									( { slug: name, kind, label, items, id, ...props }, index ) => {
										// Skip sort by.
										// It goes in the header.
										if ( 'sort-by' === id ) {
											return;
										}

										return (
											<div
												className={ classnames(
													'form-filters__group',
													{ 'border-bottom py-4': ( facets.length !== index + 1 ) },
													{ 'pt-4': ( facets.length === index + 1 ) }
												) }
												key={index}>
												<Facet
													group={name}
													kind={kind}
													label={label}
													items={items}
													{...props}
												/>
											</div>
										);
									},
								)}
						</div>
					</form>
				</div>
			</div>
		</div>
	);
};

export default Form;
